import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Phase E.4 — Client-side PDF thumbnail generator with localStorage LRU cache.
// Renders first page of PDF as a 96×128 brass-bordered thumbnail.
// Cache: keyed by document_id, dataURL stored in localStorage.
// LRU eviction when total cache > 5MB.

const CACHE_KEY = "bv_pdf_thumb_cache_v1";
const CACHE_MAX_BYTES = 5 * 1024 * 1024;
let _pdfjsPromise = null;

async function loadPdfjs() {
  if (!_pdfjsPromise) {
    _pdfjsPromise = (async () => {
      const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
      // Use worker from same bundle. Vite/CRA will resolve module URL.
      const workerUrl = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    })();
  }
  return _pdfjsPromise;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { order: [], entries: {} };
    const parsed = JSON.parse(raw);
    return { order: parsed.order || [], entries: parsed.entries || {} };
  } catch {
    return { order: [], entries: {} };
  }
}

function writeCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // quota exceeded — nuke half and retry once
    const keep = cache.order.slice(-Math.floor(cache.order.length / 2));
    const entries = {};
    keep.forEach((k) => { if (cache.entries[k]) entries[k] = cache.entries[k]; });
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ order: keep, entries })); } catch { /* give up */ }
  }
}

function getCached(id) {
  const cache = readCache();
  const hit = cache.entries[id];
  if (!hit) return null;
  // Touch LRU order
  cache.order = cache.order.filter((k) => k !== id).concat(id);
  writeCache(cache);
  return hit.dataUrl;
}

function putCached(id, dataUrl) {
  const cache = readCache();
  const size = dataUrl.length;
  cache.entries[id] = { dataUrl, size, ts: Date.now() };
  cache.order = cache.order.filter((k) => k !== id).concat(id);
  // Evict oldest until under cap
  let total = Object.values(cache.entries).reduce((s, e) => s + (e.size || 0), 0);
  while (total > CACHE_MAX_BYTES && cache.order.length > 1) {
    const evict = cache.order.shift();
    const ent = cache.entries[evict];
    delete cache.entries[evict];
    total -= (ent?.size || 0);
  }
  writeCache(cache);
}

async function renderThumb(pdfBlob) {
  const pdfjs = await loadPdfjs();
  const buf = await pdfBlob.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: buf });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1 });
  const targetW = 192; // 2x for retina, displayed at 96px
  const scale = targetW / viewport.width;
  const scaled = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(scaled.width);
  canvas.height = Math.floor(scaled.height);
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport: scaled }).promise;
  return canvas.toDataURL("image/jpeg", 0.7);
}

export default function PdfThumbnail({ documentId, filename, mimeType }) {
  const [state, setState] = useState({ status: "idle", url: null });
  const mounted = useRef(true);
  const isPdf = (mimeType === "application/pdf") || /\.pdf$/i.test(filename || "");
  const ext = (filename || "").split(".").pop()?.toUpperCase() || "FILE";

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isPdf) { setState({ status: "nonpdf", url: null }); return; }
    const cached = getCached(documentId);
    if (cached) { setState({ status: "ready", url: cached }); return; }
    setState({ status: "loading", url: null });
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await api.get(`/portal/vault/${documentId}`, { responseType: "blob" }).catch(async () => {
          // Fallback: raw fetch with cookies
          const r = await fetch(`${backend}/api/portal/vault/${documentId}`, { credentials: "include" });
          if (!r.ok) throw new Error("fetch failed");
          return { data: await r.blob() };
        });
        const dataUrl = await renderThumb(res.data);
        if (!mounted.current) return;
        putCached(documentId, dataUrl);
        setState({ status: "ready", url: dataUrl });
      } catch {
        if (mounted.current) setState({ status: "error", url: null });
      }
    })();
  }, [documentId, isPdf]);

  const frameCls = "w-[96px] h-[128px] border border-brass/60 bg-bone-dim overflow-hidden flex items-center justify-center shrink-0";
  if (state.status === "ready") {
    return (
      <div className={frameCls} data-testid={`vault-thumb-${documentId}`}>
        <img src={state.url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  if (state.status === "loading") {
    return (
      <div className={frameCls} data-testid={`vault-thumb-loading-${documentId}`}>
        <div className="text-[9px] uppercase tracking-[0.2em] text-stone">Rendering…</div>
      </div>
    );
  }
  // nonpdf, error, or idle — placeholder with extension label
  return (
    <div className={frameCls} data-testid={`vault-thumb-placeholder-${documentId}`}>
      <div className="text-center">
        <div className="font-serif text-2xl text-stone">{ext}</div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-stone mt-1">File</div>
      </div>
    </div>
  );
}
